import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { format, isValid, parseISO } from "date-fns";
import {
  ArrowLeft,
  Banknote,
  Boxes,
  Building2,
  CreditCard,
  FileText,
  BottleWine,
  Loader2,
  Mail,
  MapPin,
  Package,
  Palette,
  Receipt,
  Scale,
  Settings,
  Shield,
  Tag,
  User,
  Warehouse,
} from "lucide-react";

import { useGetLogsByEntityType, type LogEntry } from "@/api/log.api";
import { useGetLogPermissions } from "@/api/log-permissions.api";
import { useGetUsersBo } from "@/api/user.api";
import usePagination from "@/hooks/useSyncedPagination";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { DateRangePicker } from "@/components/ui/customs/date-range-picker";
import { LogDetailModal } from "@/components/shared/modal/logTimeline.modal";
import { Button } from "@/components/ui/button";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { FilterV2, useFilterV2 } from "@/components/shared/filter-v2";
import { LOG_TEMPLATES } from "@/constants/log-templates.constants";
import { useLogTemplateTexts } from "@/hooks/useLogTemplateTexts";

const ENTITY_CONFIG = {
  BILLING_ENTITY: {
    icon: <Receipt className="size-6" />,
  },
  COMPANY: {
    icon: <Building2 className="size-6" />,
  },
  CUSTOM: {
    icon: <Palette className="size-6" />,
  },
  CUSTOM_POLICY: {
    icon: <Shield className="size-6" />,
  },
  EXPEDITION: {
    icon: <Package className="size-6" />,
  },
  FILE: {
    icon: <FileText className="size-6" />,
  },
  GLOBAL_PARAMETERS: {
    icon: <Settings className="size-6" />,
  },
  IMPORTER: {
    icon: <Warehouse className="size-6" />,
  },
  INVOICE: {
    icon: <FileText className="size-6" />,
  },
  LITIGE: {
    icon: <Scale className="size-6" />,
  },
  MAILER: {
    icon: <Mail className="size-6" />,
  },
  PACKAGE_PRESET: {
    icon: <Boxes className="size-6" />,
  },
  PAYMENT: {
    icon: <CreditCard className="size-6" />,
  },
  PICKUP_LOCATION: {
    icon: <MapPin className="size-6" />,
  },
  PRODUCT: {
    icon: <BottleWine className="size-6" />,
  },
  PRODUCT_USA: {
    icon: <BottleWine className="size-6" />,
  },
  RIB: {
    icon: <Banknote className="size-6" />,
  },
  TARIF: {
    icon: <Tag className="size-6" />,
  },
  USER: {
    icon: <User className="size-6" />,
  },
} satisfies Record<string, { icon: React.ReactNode }>;

type LogEntityType = keyof typeof ENTITY_CONFIG;

const isLogEntityType = (entityType: string | null): entityType is LogEntityType =>
  entityType !== null && entityType in ENTITY_CONFIG;

const columnHelper = createColumnHelper<LogEntry>();

const LogsTableView = ({ entityType }: { entityType: string }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = usePagination("logsBo");
  const { isOpen, toggle } = useFilterV2();
  const { t } = useTranslation("boLogs");
  const { getTitle } = useLogTemplateTexts();

  const filterAction = searchParams.get("action") ?? "";
  const filterUserId = searchParams.get("userId") ?? "";
  const filterDateFrom = searchParams.get("dateFrom") ?? "";
  const filterDateTo = searchParams.get("dateTo") ?? "";

  const { data: response, isLoading } = useGetLogsByEntityType(entityType, {
    action: filterAction || undefined,
    userId: filterUserId || undefined,
    dateFrom: filterDateFrom || undefined,
    dateTo: filterDateTo || undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
  });
  const logs = useMemo(() => response?.data ?? [], [response]);

  const { data: usersBo } = useGetUsersBo();

  const logId = searchParams.get("logId");
  const selectedLog = useMemo(() => logs.find((l) => l.id === logId) ?? null, [logs, logId]);

  const updateParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };

  const userOptions = useMemo(
    () => (usersBo ?? []).map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })),
    [usersBo],
  );

  const actionOptions = useMemo(
    () =>
      Object.entries(LOG_TEMPLATES)
        .filter(([, template]) => template.entityType === entityType)
        .map(([key]) => ({ value: key, label: getTitle(key) })),
    [entityType, getTitle],
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [filterAction, filterUserId, filterDateFrom, filterDateTo, setPagination]);

  const handleDateRangeChange = (after: string | null, before: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (after && before) {
        next.set("dateFrom", after);
        next.set("dateTo", before);
      } else {
        next.delete("dateFrom");
        next.delete("dateTo");
      }
      return next;
    });
  };

  const handleReset = () => {
    setSearchParams({ entityType });
  };

  const handleLogSelect = (log: LogEntry) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("logId", log.id);
      return next;
    });
  };

  const handleLogClose = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("logId");
      return next;
    });
  };

  const columns = [
    columnHelper.display({
      id: "action",
      header: t("logsTable.columns.action"),
      cell: (info) => (
        <span className="text-sm font-medium">{getTitle(info.row.original.action)}</span>
      ),
    }),
    columnHelper.display({
      id: "user",
      header: t("logsTable.columns.user"),
      cell: (info) => {
        const u = info.row.original.user;
        return (
          <span className="text-sm text-gray-600">{u ? `${u.firstName} ${u.lastName}` : "—"}</span>
        );
      },
    }),
    columnHelper.display({
      id: "date",
      header: t("logsTable.columns.date"),
      cell: (info) => {
        const d = parseISO(info.row.original.createdAt);
        return (
          <span className="text-sm text-gray-600">
            {isValid(d) ? format(d, "dd/MM/yyyy HH:mm") : "—"}
          </span>
        );
      },
    }),
  ];

  return (
    <div className="flex flex-col gap-4">
      <FilterV2 isOpen={isOpen} onToggle={toggle} onReset={handleReset}>
        <SelectV2
          placeholder={t("logsFilters.actionPlaceholder")}
          options={actionOptions}
          value={filterAction}
          onChange={(v) => updateParam("action", v)}
          clearable
          searchable
          searchPlaceholder={t("logsFilters.actionSearchPlaceholder")}
          wrapperClassName="min-w-[220px] md:min-w-[320px]"
          contentClassName="h-[2.4rem]"
        />
        <SelectV2
          placeholder={t("logsFilters.userPlaceholder")}
          options={userOptions}
          value={filterUserId}
          onChange={(v) => updateParam("userId", v)}
          clearable
          searchable
          searchPlaceholder={t("logsFilters.userSearchPlaceholder")}
          wrapperClassName="min-w-[220px] md:min-w-[320px]"
          contentClassName="h-[2.4rem]"
        />
        <DateRangePicker
          placeholder={t("logsFilters.datePlaceholder")}
          after={filterDateFrom || null}
          before={filterDateTo || null}
          onChange={handleDateRangeChange}
        />
      </FilterV2>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <DataTable
          data={logs}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          rowCount={response?.count ?? 0}
          autoResetPageIndex={false}
          onRowClick={handleLogSelect}
        />
      )}

      <LogDetailModal log={selectedLog} open={!!selectedLog} onClose={handleLogClose} />
    </div>
  );
};

export const LogsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEntityType = searchParams.get("entityType");
  const { data: permissions } = useGetLogPermissions();
  const { t } = useTranslation(["boLogs", "common"]);
  const { getEntityLabel } = useLogTemplateTexts();

  const visibleEntityTypes = useMemo(() => {
    if (!permissions) return new Set<string>();
    const allowedActions = new Set(permissions.filter((p) => p.isBo).map((p) => p.action));
    return new Set(
      Object.entries(LOG_TEMPLATES)
        .filter(([key]) => allowedActions.has(key))
        .map(([, template]) => template.entityType),
    );
  }, [permissions]);

  const visibleEntries = useMemo(
    () =>
      (Object.keys(ENTITY_CONFIG) as LogEntityType[]).filter((key) => visibleEntityTypes.has(key)),
    [visibleEntityTypes],
  );

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-6 p-8">
      <div className="flex justify-between gap-2">
        <div className="flex flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("page.title")}</h1>
          <p className="text-gray-500">{t("page.subtitle")}</p>
        </div>
        {selectedEntityType && (
          <Button variant="outline" onClick={() => setSearchParams({})} className="gap-2 self-end">
            <ArrowLeft className="h-4 w-4" />
            {t("common:actions.back")}
          </Button>
        )}
      </div>

      {selectedEntityType && (
        <div className="flex items-center gap-3 rounded-lg border bg-white p-4 shadow-xs">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            {isLogEntityType(selectedEntityType) ? (
              ENTITY_CONFIG[selectedEntityType].icon
            ) : (
              <Package className="size-6" />
            )}
          </div>
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-semibold text-gray-900">
              {getEntityLabel(selectedEntityType)}
            </p>
            <p className="text-xs text-gray-500">
              {isLogEntityType(selectedEntityType)
                ? t(`entityDescriptions.${selectedEntityType}`)
                : ""}
            </p>
          </div>
        </div>
      )}

      {selectedEntityType ? (
        <LogsTableView entityType={selectedEntityType} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleEntries.map((key) => (
            <button
              key={key}
              onClick={() => setSearchParams({ entityType: key })}
              className="flex flex-col gap-3 rounded-lg border bg-white p-5 text-left shadow-xs transition-colors hover:border-blue-200 hover:bg-blue-50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                {ENTITY_CONFIG[key].icon}
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-900">{getEntityLabel(key)}</p>
                <p className="line-clamp-2 text-xs text-gray-500">
                  {t(`entityDescriptions.${key}`)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
